import { createAsyncThunk } from '@reduxjs/toolkit';
import { loadChatUsers } from './loadChatUsers';
import { getProjectUsersThunk } from './getProjectUsers';
import { setThread, setProject } from '../slices/chatSlice';
import { getProjectSubthreadsThunk } from './getProjectSubthreads';
import { selectProjectById } from 'store/projects/selectors/projectsSelectors';

/**
 * Loads users for a thread when it's set as the active thread.
 * For subthreads (isSubthread === true), loads users by their userIds.
 * For regular project threads, loads all project users.
 */
export const loadThreadUsersThunk = createAsyncThunk(
  'chat/loadThreadUsers',
  async ({ thread }, { dispatch }) => {
    if (!thread) {
      return;
    }

    // For subthreads or private threads with specific users, load those users
    if (thread.isSubthread === true || thread.userIds) {
      if (thread.userIds && thread.userIds.length > 0) {
        await dispatch(
          loadChatUsers({
            userIds: thread.userIds,
            addToCurrentUsers: true,
          })
        );
      }
    }

    if (thread.projectId) {
      await dispatch(
        getProjectUsersThunk({
          projectId: thread.projectId,
          addToCurrentUsers: !thread.isSubthread,
        })
      );
    }
  }
);

/**
 * Sets a thread as the active thread and loads its users automatically.
 * This is a convenience thunk that combines setThread with loadThreadUsers.
 * For main project threads, also loads the subthreads.
 */
export const setThreadWithUsers = createAsyncThunk(
  'chat/setThreadWithUsers',
  async ({ thread }, { dispatch, getState }) => {
    // First set the thread
    dispatch(setThread(thread));

    // Set the project if the thread has a projectId
    if (thread?.projectId) {
      const state = getState();
      const project = selectProjectById(thread.projectId)(state);
      if (project && project.id) {
        dispatch(setProject(project));
      }
    }

    // Then load the users for this thread
    await dispatch(loadThreadUsersThunk({ thread }));

    // If this is a main project thread (not a subthread), load its subthreads
    if (thread?.projectId && !thread.isSubthread) {
      dispatch(getProjectSubthreadsThunk({ projectId: thread.projectId }));
    }
  }
);
